import type { ForecastDaySummary, ForecastHourSummary, ForecastVariable } from './WeatherDashboard';

export interface ChartPoint {
  key: string;
  label: string;
  value: number | null;
}

export interface ChartTheme {
  stroke: string;
  fillTop: string;
  fillBottom: string;
  dot: string;
}

export const CHART_THEMES: Record<ForecastVariable, ChartTheme> = {
  temp: {
    stroke: '#ca8a04',
    fillTop: 'rgba(234, 179, 8, 0.35)',
    fillBottom: 'rgba(234, 179, 8, 0.02)',
    dot: '#a16207',
  },
  precip: {
    stroke: '#0284c7',
    fillTop: 'rgba(14, 165, 233, 0.35)',
    fillBottom: 'rgba(14, 165, 233, 0.02)',
    dot: '#0369a1',
  },
  wind: {
    stroke: '#7c3aed',
    fillTop: 'rgba(139, 92, 246, 0.3)',
    fillBottom: 'rgba(139, 92, 246, 0.02)',
    dot: '#6d28d9',
  },
};

export function chartValue(
  variable: ForecastVariable,
  row?: ForecastDaySummary | ForecastHourSummary | null,
): number | null {
  if (!row) return null;
  if (variable === 'temp' && row.temperature_c != null) return row.temperature_c;
  if (variable === 'precip' && row.precipitation_mm != null) return row.precipitation_mm;
  if (variable === 'wind' && row.wind_speed_ms != null) return row.wind_speed_ms;
  return null;
}

export function formatChartValue(variable: ForecastVariable, value: number): string {
  if (variable === 'temp') return `${Math.round(value)}°`;
  if (variable === 'precip') return value < 1 ? value.toFixed(1) : `${value.toFixed(0)}`;
  return value.toFixed(1);
}

export function formatHourLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
}

export function periodFromIso(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return d.toISOString().slice(0, 10);
}

export function filterHoursForDay(hours: ForecastHourSummary[], period: string): ForecastHourSummary[] {
  return hours.filter((h) => periodFromIso(h.valid_at) === period);
}

export function dayHiLoFromHours(
  hours: ForecastHourSummary[],
  period: string,
): { hi: number; lo: number } | null {
  const temps = filterHoursForDay(hours, period)
    .map((h) => h.temperature_c)
    .filter((t): t is number => t != null);
  if (temps.length === 0) return null;
  return { hi: Math.max(...temps), lo: Math.min(...temps) };
}

export function buildWeekChartPoints(
  variable: ForecastVariable,
  periods: string[],
  days: ForecastDaySummary[],
): ChartPoint[] {
  return periods.map((period) => {
    const day = days.find((d) => d.period === period);
    const d = new Date(`${period}T12:00:00`);
    const label = Number.isNaN(d.getTime())
      ? period
      : d.toLocaleDateString(undefined, { weekday: 'short' });
    return { key: period, label, value: chartValue(variable, day) };
  });
}

export function buildDayChartPoints(
  variable: ForecastVariable,
  hours: ForecastHourSummary[],
  period: string,
): ChartPoint[] {
  return filterHoursForDay(hours, period).map((hour) => ({
    key: hour.valid_at,
    label: formatHourLabel(hour.valid_at),
    value: chartValue(variable, hour),
  }));
}

export function computeYDomain(values: number[], variable: ForecastVariable): [number, number] {
  if (values.length === 0) return variable === 'precip' ? [0, 5] : [0, 10];
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min;
  const pad = span < 0.5 ? (variable === 'precip' ? 0.5 : 1) : span * 0.15;
  const lo = variable === 'precip' ? 0 : min - pad;
  const hi = max + pad;
  return [lo, hi];
}

/** Pick ~6 evenly spaced x-axis labels from chart points. */
export function pickAxisLabels(points: ChartPoint[], maxLabels = 7): ChartPoint[] {
  if (points.length <= maxLabels) return points;
  const step = Math.max(1, Math.floor((points.length - 1) / (maxLabels - 1)));
  const picked: ChartPoint[] = [];
  for (let i = 0; i < points.length; i += step) picked.push(points[i]!);
  const last = points[points.length - 1]!;
  if (picked[picked.length - 1]?.key !== last.key) picked.push(last);
  return picked;
}
